import React, { useEffect, useState } from 'react';
import { Button, InputNumber, Modal, Spin, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import TopNav from '../components/top-nav';
import FooterDisplay from '../components/footer';
import { _create_fourward_deposit, _get_fourward_account, _get_fourward_config, _get_fourward_quote } from '../common/axios_services';

const FourwardPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [deposit, setDeposit] = useState('');
  const [quote, setQuote] = useState(null);
  const [account, setAccount] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  useEffect(() => {
    Promise.all([_get_fourward_account(), _get_fourward_config()])
      .then(([accountResponse, configResponse]) => {
        setAccount(accountResponse?.data?.data || accountResponse?.data);
        setConfig(configResponse?.data?.data || configResponse?.data);
      })
      .catch((error) => message.error(error?.response?.data?.message || 'Unable to load 4Ward.'))
      .finally(() => setLoading(false));
  }, []);

  const calculateQuote = async () => {
    try {
      const response = await _get_fourward_quote(Number(deposit));
      setQuote(response?.data?.data || response?.data);
      setStep(2);
    } catch (error) {
      message.error(error?.response?.data?.message || 'Unable to calculate 4Ward value.');
    }
  };

  const submitDeposit = async () => {
    setSubmitting(true);
    try {
      await _create_fourward_deposit(Number(deposit));
      message.success('4Ward deposit created successfully.');
      const refreshed = await _get_fourward_account();
      setAccount(refreshed?.data?.data || refreshed?.data);
      setStep(1);
      setDeposit('');
      setQuote(null);
    } catch (error) {
      message.error(error?.response?.data?.message || 'Unable to create 4Ward deposit.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatAccountNumber = (raw) => {
    if (!raw) return '—';
    if (raw.includes('-')) {
      const numOnly = raw.replace(/\D/g, '');
      return (numOnly.slice(0, 8) || '00000000').padStart(8, '0');
    }
    return raw;
  };

  const formatAmount = (value) => `NGN ${Number(value || 0).toLocaleString()}`;

  const formatDate = (value) => value
    ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
    : '—';

  if (loading) return <div className="center-content"><Spin /></div>;

  return (
    <div className="wise9ja-content min-h-screen pb-40 pt-28">
      <TopNav title="Personal 4Ward" />
      <main className="contain space-y-5">
        <section className="bg-green-50 border border-green-200 rounded-2xl p-6">
          <p className=" text-gray-600 text-[13px]">4Ward Account Number</p>
          <p className="text-3xl font-gilroyBold text-gray-900 tracking-wider">{formatAccountNumber(account?.account?.accountNumber)}</p>
          <p className="text-gray-600 mt-2 text-[13px]">Your 4Ward account number is your Konnect ID.</p>
        </section>

        {step === 1 ? (
          <section className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-2xl font-gilroyBold m-0">How Personal 4Ward Works</h2>
              <Button type="link" icon={<QuestionCircleOutlined />} onClick={() => setHowItWorksOpen(true)} className="font-gilroyBold whitespace-nowrap px-0">
                How it works
              </Button>
            </div>
            <p className="text-gray-600 text-[13px]">4Ward is for checkout. Transfers from 4Ward to 4Ward are instant value transfers.</p>
            <p className="text-gray-600 text-[13px]">Choose a deposit and receive an Appreciated Value at the maturity date.</p>
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 space-y-4">
              <div>
                <p className="font-gilroyBold text-gray-900 text-[13px]">Current 4Ward settings</p>
                <p className="text-gray-600  text-[13px] mt-1">These values are controlled by the administrator rate configuration.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-white border border-blue-100 p-3">
                  <p className="text-gray-500  text-[13px]">Minimum deposit</p>
                  <p className="font-gilroyBold text-gray-900 mt-1">{formatAmount(config?.minimumDeposit)}</p>
                </div>
                <div className="rounded-xl bg-white border border-blue-100 p-3">
                  <p className="text-gray-500  text-[13px]">Maturity period</p>
                  <p className="font-gilroyBold text-gray-900 mt-1">{config?.maturityDays || 0} days</p>
                </div>
              </div>
              <div>
                <p className="text-gray-600  text-[13px] mb-2">Applicable 4Ward rates</p>
                <div className="space-y-2">
                  {(config?.bands || []).map((band) => (
                    <div key={band.key} className="flex items-center justify-between rounded-lg bg-white border border-blue-100 px-3 py-2">
                      <span className="text-gray-600  text-[13px]">{band.max ? `${formatAmount(band.min)} - ${formatAmount(band.max)}` : `Above ${formatAmount(band.min - 1)}`}</span>
                      <span className="font-gilroyBold text-green-700">{Number(band.rate || 0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-gray-600  text-[13px]">Your APV is calculated from the deposit and the applicable rate, then credited at maturity.</p>
            </div>
            <Button type="primary" size="large" onClick={() => setStep(2)} className="bg-green-600 w-full">Continue</Button>
          </section>
        ) : (
          <section className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
            <h2 className="text-2xl font-gilroyBold">Create 4Ward Deposit</h2>
            <label className="block font-gilroyBold text-gray-700">Deposit amount</label>
            <InputNumber
              size="large"
              className="w-full"
              prefix="₦"
              min={0}
              value={deposit}
              onChange={(val) => setDeposit(val)}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
              placeholder="Enter deposit amount"
            />
            {!quote ? <Button type="primary" size="large" onClick={calculateQuote} className="bg-green-600 w-full">Calculate Maturity Value</Button> : (
              <div className="space-y-4">
                <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <p className="text-gray-600 text-[13px]">Your 4Ward projection</p>
                      <p className="text-2xl font-gilroyBold text-gray-900 mt-1">{formatAmount(quote.apv)}</p>
                    </div>
                    <span className="rounded-full bg-green-600 px-3 py-1 text-[13px] font-gilroyBold text-white">{quote.rate}% rate</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-xl bg-white border border-green-100 p-3">
                      <p className="text-gray-500 text-[13px]">Deposit</p>
                      <p className="font-gilroyBold text-[13px text-gray-900 mt-1">{formatAmount(quote.deposit)}</p>
                    </div>
                    <div className="rounded-xl bg-white border border-green-100 p-3">
                      <p className="text-gray-500 text-[13px]">Available at maturity</p>
                      <p className="font-gilroyBold text-[13px text-green-700 mt-1">{formatDate(quote.maturityDate)}</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-500 text-[13px]">Your deposit is held until the maturity date. The appreciated value will then be credited to your 4Ward balance.</p>
                <Button type="primary" size="large" loading={submitting} onClick={submitDeposit} className="bg-green-600 w-full">Confirm Deposit</Button>
              </div>
            )}
            <Button type="link" onClick={() => { setQuote(null); setStep(1); }}>Back</Button>
          </section>
        )}

        <section className="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 className="text-2xl font-gilroyBold mb-4">4Ward History</h2>
          {(account?.transactions || []).length === 0 ? <p className="text-gray-500 text-[13px]">No 4Ward history available.</p> : (account.transactions || []).map((item) => (
            <div key={item.id} className="border-b border-gray-100 py-3 last:border-0">
              <div className="flex justify-between"><strong>{item.type}</strong><span>₦{Number(item.amount).toLocaleString()}</span></div>
              <p className="text-gray-500">Deposit: ₦{Number(item.depositAmount || 0).toLocaleString()} · APV: ₦{Number(item.apvAmount || 0).toLocaleString()}</p>
              <p className="text-gray-500">Maturity: {formatDate(item.maturityDate)} · Status: {item.status || '—'}</p>
            </div>
          ))}
        </section>
        <Button type="link" onClick={() => navigate('/wallet')}>Back to Wallet</Button>
      </main>
      <Modal
        title="How 4Ward Works"
        open={howItWorksOpen}
        onCancel={() => setHowItWorksOpen(false)}
        footer={null}
        centered
      >
        <p className="text-gray-700">4Ward is Konnect's smart feature that allows users and groups to lock funds today and receive higher spending value at a future date.</p>
        <ol className="space-y-3 text-gray-700 list-decimal pl-5 pt-2">
          <li>Every user and group automatically receives a 4Ward account linked to their Konnect ID.</li>
          <li>Funds are deposited into 4Ward and assigned a maturity period.</li>
          <li>Konnect calculates an Appreciated Value (APV) based on the deposit amount and applicable rate.</li>
          <li>On maturity, the APV is automatically credited to the 4Ward balance.</li>
          <li>4Ward balances are designed primarily for purchases and checkout transactions within Konnect.</li>
          <li>Users and groups can track deposits, appreciation earned, maturity dates, credits, debits, and transaction history.</li>
        </ol>
      </Modal>
      <FooterDisplay />
    </div>
  );
};

export default FourwardPage;
